<template>
    <div v-if="hotelInfo" class="hotel-room full-width">
        <div class="d-flex flex-column flex-md-row">
            <div class="hotel-room__info-wrapper d-flex flex-column flex-md-row">
                <v-img
                    v-if="groupBy !== 'name' && imgSrc"
                    tile
                    class="me-md-2 cursor-pointer"
                    :src="$filters.imageUrl(imgSrc, 300)"
                    :alt="room.name"
                    eager
                    :width="150"
                    :max-width="150"
                    height="100"
                    :lazy-src="$filters.srcHost('/placeholder.png')"
                    @click="roomInfo"
                />
                <div class="hotel-room__info text-caption">
                    <hotel-room-name-label
                        v-if="groupBy !== 'name'"
                        class="hotel-room__name"
                        :hotel="hotel"
                        :room="room"
                        :compare-mode="compareMode"
                    >
                        <template v-slot>
                            <div class="d-flex align-center font-weight-bold cursor-pointer mb-1" @click="roomInfo">
                                <v-icon size="small" start>mdi-information-outline</v-icon>
                                {{ room.name }}
                                <ProductOfferTipsDialog
                                    v-if="room.tipsAvailable"
                                    :offer-key="room.groupedOffers[0].offerKey"
                                />
                                <v-tooltip v-if="isShowDynamicRate" :text="$t('dynamic_rate')" location="bottom">
                                    <template v-slot:activator="{props}">
                                        <v-icon class="ms-1" v-bind="props" color="primary"> mdi-sync </v-icon>
                                    </template>
                                    <span>{{ $t('dynamic_rate') }}</span>
                                </v-tooltip>
                            </div>
                            <supplier-name-label
                                v-if="isMultipleSuppliers && isTO1"
                                :supplier-name="room.hotelSupplierName"
                                class="ml-6"
                            />
                        </template>
                    </hotel-room-name-label>
                    <special-offer-label v-if="showSpecialOfferLabel && discountDetails" :details="discountDetails" />
                    <hotel-available-label v-if="!room.soldOut" class="hotel-room__available" :room="room" />
                    <hotel-meal-type-label class="hotel-room__meal" :hotel="hotel" :room="room" />
                    <tariff-label :hotel="hotel" :room="room" class="hotel-room__tariff" />
                    <conditions-label
                        v-if="(room && room.price) || ($conf.public.packagesNotDeltaPrice && room.notDeltaPrice)"
                        class="hotel-room__conditions"
                        :offer-key="room.groupedOffers[0].offerKey"
                        :price="room.price || room.notDeltaPrice"
                        :offer="hotel"
                        @conditions="showConditionsInfo"
                    />
                    <div class="hotel-room__info-icons d-flex">
                        <div v-if="room.bedroomsQuantity" class="me-3 d-flex">
                            <v-icon color="info" :title="$t('bedrooms_quantity')" start>mdi-bed-outline</v-icon>
                            {{ room.bedroomsQuantity }}
                        </div>
                        <div v-if="room.bathroomsQuantity" class="me-3 d-flex">
                            <v-icon color="info" :title="$t('bathrooms_quantity')" start>mdi-shower</v-icon>
                            {{ room.bathroomsQuantity }}
                        </div>
                        <hotel-bed-type-label v-if="room.bedTypes && room.bedTypes.length" :bed-types="room.bedTypes" />
                    </div>
                    <div v-if="accommodationText" class="font-italic">
                        {{ accommodationText }}
                    </div>
                </div>
            </div>
            <div v-if="!room.soldOut" class="price-col ms-auto">
                <template v-if="!isMultiRoom">
                    <slot name="price" :room="room">
                        <hotel-room-offer-price-block :offer="hotel" :room="room" :search-request="searchRequest" />
                    </slot>
                </template>
                <template v-else>
                    <hotel-room-offer-price-block
                        v-if="!isPackage"
                        :offer="hotel"
                        :room="room"
                        :search-request="searchRequest"
                        :rph="rph"
                    />
                    <slot v-else name="price" :room="room" />
                </template>
            </div>
            <div v-else class="price-col ms-auto">
                <sold-out-modal small :reason="room.soldOutReason" />
            </div>
            <span
                v-if="room.price && room.price.discountDetails && room.specialOffer && isMobile"
                class="text-success text-caption font-weight-medium"
            >
                {{ room.price.discountDetails }}
            </span>
        </div>
    </div>
</template>

<script lang="ts">
    import {Component, Prop, Vue} from 'vue-facing-decorator'
    import HotelRoomNameLabel from '@/components/hotels/snippets/HotelRoomNameLabel.vue'
    import ConditionsLabel from '~src/components/snippets/conditionsLabel.src'
    import {EventBus, SHOW_HOTEL_CONDITIONS_INFO, SHOW_ROOM_INFO_MODAL_EVENT} from '@/utils/event-bus'
    import HotelRoomOfferPriceBlock from '~src/components/search/offers/hotelRoomOfferPriceBlock.src'
    import HotelMealTypeLabel from '~src/components/hotels/snippets/hotelMealTypeLabel.src'
    import HotelBedTypeLabel from '@/components/hotels/snippets/HotelBedTypeLabel.vue'
    import TariffLabel from '~src/components/hotels/snippets/tariffLabel.src'
    import HotelAvailableLabel from '~src/components/hotels/snippets/hotelAvailableLabel.src'
    import SpecialOfferLabel from '~src/components/hotels/search/offers/specialOfferLabel.src'
    import SupplierNameLabel from '@/components/hotels/snippets/SupplierNameLabel.vue'

    import SoldOutModal from '~src/components/snippets/soldOutModal.src'
    import ProductOfferTipsDialog from '~/components/modals/ProductOfferTipsDialog.vue'

    @Component({
        components: {
            SpecialOfferLabel,
            HotelRoomNameLabel,
            HotelAvailableLabel,
            HotelMealTypeLabel,
            HotelBedTypeLabel,
            ConditionsLabel,
            HotelRoomOfferPriceBlock,
            TariffLabel,
            SupplierNameLabel,
            SoldOutModal,
            ProductOfferTipsDialog,
        },
    })
    export class HotelRoom extends Vue {
        @Prop({default: false, type: Boolean}) compareMode
        @Prop({required: true}) hotel
        @Prop({required: true}) room
        @Prop({required: true}) searchRequest
        @Prop({default: 'none'}) groupBy
        @Prop() rph
        @Prop({default: false, type: Boolean}) isPackage

        showConditionsInfo() {
            EventBus.$emit(SHOW_HOTEL_CONDITIONS_INFO, {
                searchRequest: this.searchRequest,
                hotel: this.hotel,
                room: this.room,
            })
        }

        roomInfo() {
            EventBus.$emit(SHOW_ROOM_INFO_MODAL_EVENT, {
                hotel: this.hotel,
                room: this.room,
            })
        }

        get hotelInfo() {
            return hotelsRuntimeStore.getHotelsInfoByRoomOffer(this.room, this.hotel)
        }

        get isShowDynamicRate() {
            return !!this.room.packageOnly
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }

        get imgSrc() {
            const roomType = this.hotelInfo?.hotelDescriptionInfo?.roomTypes?.roomType.find(
                roomType => roomType.code === this.room.code || roomType.code === this.room.roomCode
            )

            return roomType?.images.image[0]?.url
        }

        get isMultiRoom() {
            return this.searchRequest.rooms?.length > 1
        }

        get accommodationText() {
            if (this.room.onExtraBed && this.room.onWithoutPlace) {
                return this.$t('accommodated_on_extra_bed_or_without_bed')
            } else if (this.room.onExtraBed && !this.room.onWithoutPlace) {
                return this.$t('accommodated_on_extra_bed')
            } else if (!this.room.onExtraBed && this.room.onWithoutPlace) {
                return this.$t('accommodated_without_bed')
            }

            return ''
        }

        get isMultipleSuppliers() {
            return this.hotel.rooms?.some(room => room.hotelSupplierName !== this.hotel.supplierName)
        }

        get isTO1() {
            return authStore.isTO1
        }

        get discountDetails() {
            return (
                this.room.price?.discountDetails ||
                this.room.discountDetails ||
                this.room.notDeltaPrice?.discountDetails
            )
        }

        get showSpecialOfferLabel() {
            return (
                (this.isPackage && this.discountDetails) ||
                (!this.room.soldOut && (this.room.specialOffer || this.room.price?.discountDetails))
            )
        }
    }

    export default HotelRoom
</script>

<style scoped lang="scss">
    .price-col {
        width: 180px;

        .show-rooms-label__short {
            display: none;
        }
    }

    //TODO Remove after layout redesign
    @media (max-width: 1440px) {
        .price-col {
            width: 130px;

            .show-rooms-label {
                display: none;

                &__short {
                    display: inline;
                }
            }
        }
    }
</style>
